import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MatStepper } from '@angular/material';
import { ToasterService } from 'angular2-toaster';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { finalize } from 'rxjs/operators';
import { AdvogadoDTO } from 'src/app/model/dto/advogado.dto';
import { AdvogadoService } from 'src/app/service/advogado/advogado.service';
import { AtivacaoTokenService } from 'src/app/service/ativacaotoken/ativacao-token.service';
import { MaskUtil } from 'src/app/utils/mask-util';


@Component({
  selector: 'app-cadastro-dialog',
  templateUrl: './cadastro-dialog.component.html',
  styleUrls: ['./cadastro-dialog.component.css']
})

export class CadastroDialogComponent implements OnInit {

  @BlockUI() blockUI: NgBlockUI;
  @ViewChild("stepper") cadastroAdvogadoStepper: MatStepper;

  // Form Cadastro Advogado
  cadastroForm: FormGroup;
  cpf = this.fb.control('', [Validators.required, this.cpfValidator()]);
  oab = this.fb.control('', [Validators.required, this.oabValidator()]);
  nome = this.fb.control('', Validators.required);
  email = this.fb.control('', [Validators.required, Validators.email]);
  celular = this.fb.control('', [Validators.required, this.celularValidator()]);
  codigoConfirmacaoEmail = this.fb.control('', [Validators.required]);

  // Controles para stepper
  emailConfirmado = false;
  isEditable = false;
  isLoading = false;


  constructor(
    private dialogRef: MatDialogRef<CadastroDialogComponent>,
    private toasterService: ToasterService,
    private advogadoService: AdvogadoService,
    private ativacaoTokenService: AtivacaoTokenService,
    private fb: FormBuilder) { }


  ngOnInit(): void {
    this.initForm();
  }


  initForm() {
    this.cadastroForm = this.fb.group({
      cpf: this.cpf,
      oab: this.oab,
      nome: this.nome,
      email: this.email,
      celular: this.celular
    });
  }


  async cadastrar() {
    if (!this.cadastroForm.valid) return;
    this.isLoading = true;
    this.isEditable = false;
    const adovgadoJaCadastrado = await this.advogadoEstaCadastrado(this.cpf.value, this.email.value);

    if (!adovgadoJaCadastrado) {
      this.ativacaoTokenService.gerarToken(this.email.value)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe({
          next: () => this.cadastroAdvogadoStepper.next(),
          error: (_) => this.toasterService.pop("warning", "Não foi possível gerar o Token de confirmação, tente novamente mais tarde.")
        });
    };
  }

  confirmarCodigoEmail() {
    if (this.codigoConfirmacaoEmail.invalid) return;

    this.isLoading = true;
    const { value: token } = this.codigoConfirmacaoEmail;
    const { value: email } = this.email;

    this.ativacaoTokenService.validarCodigo(token, email)
      .subscribe({
        next: () => this.criarCadastro(),
        error: () => this.informarCodigoInvalido()
      });
  }

  criarCadastro(): void {
    const { cpf, oab, nome, email, celular } = this;
    const advogadoDTO = new AdvogadoDTO(
      undefined,
      cpf.value,
      oab.value,
      nome.value,
      email.value,
      celular.value
    );

    this.isLoading = true;
    this.advogadoService.criar(advogadoDTO)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          this.isEditable = false;
          this.cadastroAdvogadoStepper.next();
        },
        error: (err) => {
          this.toasterService.pop('warning', err.error);
        },
      });
  }

  informarCodigoInvalido(): void {
    this.codigoConfirmacaoEmail.setErrors(['required']);
    this.toasterService.pop('warning', 'Código de confirmação de e-mail inválido.');
    this.isEditable = true;
    this.isLoading = false;
  }


  async advogadoEstaCadastrado(cpf: string, email: string) {
    try {
      await this.advogadoService.verificarExistenciaAdvogado(cpf, email).toPromise();
      return false;
    } catch ({ status }) {

      if (status === 400) {
        this.informarCpfOuEmailJaCadastrados();
      } else {
        this.toasterService.pop('warning', 'Ocorreu um erro, tente novamente em instantes.');
      }

      this.isLoading = false
      return true;
    }
  }

  informarCpfOuEmailJaCadastrados() {
    this.cpf.setErrors(['existingCpf']);
    this.email.setErrors(['existingEmail']);
    this.toasterService.pop('error', 'CPF e/ou Email já cadastrado.');
  }

  voltarEtapaDadosPessoais() {
    this.cadastroAdvogadoStepper.previous();
    this.codigoConfirmacaoEmail.reset();
  }

  onCancel() {
    this.dialogRef.close();
  }

  cpfValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const cpfValue = control.value;
      if (!cpfValue) return null;

      if (!MaskUtil.validar_cpf(cpfValue)) {
        return { pattern: true };
      }

      return null;
    };
  }

  celularValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const celularValue = control.value;
      if (!celularValue) return null;
      const celular = celularValue.replace(/\D/g, '');
      if (celular.length !== 11) return { pattern: true };
      return null;
    };
  }

  convertToUpperCase(value: string): void {
    const upperCaseValue = value.toUpperCase();
    this.oab.setValue(upperCaseValue, { emitEvent: false });
  }

  oabValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const oabValue = control.value;
      if (!oabValue) return null;
      const regex = /^[A-Z]{2}\d{3,6}[A-Z]?$/;
      if (!regex.test(oabValue)) return { pattern: true };
      return null;
    };
  }

}
